import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const TOPICS = [
  "Financial Technology",
  "AI Innovations",
  "Product Releases",
  "Industry Insights",
  "Events",
  "Partner News",
  "Regulatory Updates",
];

function Newsletter() {
  const reduce = useReducedMotion();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!email.trim()) return;
    setSent(true);
  };

  return (
    <section
      id="newsletter"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="newsletter-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_100%_0%,rgba(229,184,11,0.12),transparent_55%),radial-gradient(50%_40%_at_0%_80%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.32]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid items-end gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-16">
          <motion.header
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Stay updated
            </p>
            <h2
              id="newsletter-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Subscribe to our
              <span className="mt-1 block">newsletter</span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-navy/55 sm:text-lg">
              Receive the latest updates on the ecosystem, products, and the market we serve.
            </p>
          </motion.header>

          <motion.form
            className="w-full"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
            onSubmit={onSubmit}
            noValidate
          >
            <label
              htmlFor="newsletter-email"
              className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40"
            >
              Email address
              <span className="text-gold"> *</span>
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-stretch">
              <input
                id="newsletter-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (sent) setSent(false);
                }}
                placeholder="you@company.com"
                className="min-h-12 flex-1 border border-navy/15 bg-[#f3f7f5] px-4 text-sm text-navy outline-none transition placeholder:text-navy/30 focus:border-gold focus:ring-2 focus:ring-gold/40"
              />
              <button
                type="submit"
                className="inline-flex min-h-12 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full bg-navy px-6 text-sm font-semibold text-white transition hover:bg-royal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
              >
                Subscribe Now
                <ArrowIcon />
              </button>
            </div>
            {sent ? (
              <p className="mt-3 text-sm text-navy/55" role="status">
                You’re on the list. We’ll send the next briefing to this address.
              </p>
            ) : (
              <p className="mt-3 text-sm text-navy/40">Required. We won’t share your address.</p>
            )}
          </motion.form>
        </div>

        <motion.div
          className="mt-14"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
            Receive the latest updates on
          </p>
          <ol className="mt-6 grid grid-cols-2 gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-3 lg:grid-cols-7">
            {TOPICS.map((topic, i) => (
              <motion.li
                key={topic}
                className="bg-white px-4 py-5"
                initial={reduce ? false : { opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={
                  reduce ? { duration: 0 } : { duration: 0.4, delay: 0.04 * i, ease: EASE }
                }
              >
                <span className="block font-mono text-[11px] font-semibold tracking-[0.18em] text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="mt-3 block text-sm font-semibold leading-snug tracking-tight text-navy">
                  {topic}
                </span>
              </motion.li>
            ))}
          </ol>
        </motion.div>
      </div>
    </section>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Newsletter;
